import { AuthService, LoggerService, Unity4Claim } from '@adreno-common';
import { Injectable } from '@angular/core';
import { TeleworkerState } from '@app/core/models';
import { CoreService, UserProfileService } from '@app/core/services';
import {
  BehaviorSubject,
  EMPTY,
  Observable,
  combineLatest,
  merge,
  timer,
} from 'rxjs';
import {
  filter,
  map,
  pairwise,
  switchMap,
  take,
  tap,
  timeInterval,
  withLatestFrom,
} from 'rxjs/operators';
import { IdleCheckModalComponent } from '../components';
import { IdleReturnValue } from '../models';
import { ToastrService } from 'ngx-toastr';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

@Injectable({
  providedIn: 'root',
})
export class IdleService {
  private agentInteraction$: Observable<void>;
  private agentInteractionSub: BehaviorSubject<void>;
  private preIdleState?: TeleworkerState;

  constructor(
    private authSvc: AuthService,
    private userSvc: UserProfileService,
    private coreSvc: CoreService,
    private logger: LoggerService,
    private toastr: ToastrService,
    private modalSvc: NgbModal
  ) {
    this.agentInteractionSub = new BehaviorSubject<void>(void 0);
    this.agentInteraction$ = this.agentInteractionSub.asObservable();

    const filteredState$ = this.coreSvc.state$.pipe(
      filter(
        (state) =>
          !!state && ![TeleworkerState.CallDisconnected].includes(state)
      )
    );

    combineLatest([
      this.authSvc.hasClaim$(Unity4Claim.IdleCheck),
      this.userSvc.userProfile$,
    ])
      .pipe(
        filter(
          ([hasIdleCheckClaim, userProfile]) =>
            hasIdleCheckClaim && userProfile.country == 'Australia'
        ),
        take(1),
        switchMap(() =>
          combineLatest([
            filteredState$.pipe(
              filter(
                (state) =>
                  !!state && ![TeleworkerState.CallDisconnected].includes(state)
              ),
              timeInterval(),
              pairwise(),
              tap((data) =>
                this.logger.debug('IdleService', 'presenceInterval', { data })()
              )
            ),
            this.agentInteraction$.pipe(
              tap(() => this.logger.debug('IdleService', 'agentInteraction')())
            ),
          ])
        ),
        withLatestFrom(this.coreSvc.campaignId$),
        switchMap(
          ([
            [[previousStateInterval, currentStateInterval], _],
            campaignId,
          ]) => {
            if (
              !!currentStateInterval.value &&
              [
                TeleworkerState.WrapIB,
                TeleworkerState.WrapOB,
                TeleworkerState.Preview,
                TeleworkerState.Tasks,
              ].includes(currentStateInterval.value)
            ) {
              let durationSec = 20;
              if (
                currentStateInterval.value == TeleworkerState.Preview ||
                currentStateInterval.value == TeleworkerState.Tasks
              ) {
                durationSec = 20;
              } else if (
                (previousStateInterval.value ==
                  TeleworkerState.InboundConnected ||
                  previousStateInterval.value ==
                    TeleworkerState.OutboundConnected) &&
                currentStateInterval.interval >= 60 * 1000
              ) {
                durationSec = 40;
              }
              this.logger.debug('IdleService', 'starting timer', {
                previousStateInterval,
                currentStateInterval,
                campaignId,
                durationSec,
              })();
              return timer(durationSec * 1000);
            } else {
              return EMPTY;
            }
          }
        ),
        switchMap((data) => {
          this.logger.warn('IdleService', 'showIdleModal$', { data })();

          return this.showIdleModal$();
        }),
        withLatestFrom(filteredState$)
      )
      .subscribe(([idleRetVal, state]) => {
        this.logger.info('IdleService', 'idle timeout', { idleRetVal })();
        if (idleRetVal == 'timeout') {
          this.preIdleState = state;
          this.coreSvc.state = TeleworkerState.Idle;
          this.toastr.info('Your presence state has been set to Idle.');
        } else if (idleRetVal == 'still-here') {
          this.agentInteraction(); //restarts the idle timer
        } else if (this.preIdleState) {
          this.coreSvc.state = this.preIdleState;
          this.preIdleState = undefined;
        }
      });
  }

  private showIdleModal$(): Observable<IdleReturnValue> {
    const modalRef = this.modalSvc.open(IdleCheckModalComponent, {
      size: 'sm',
      backdrop: 'static',
      keyboard: false,
    });
    return merge(
      timer(10 * 1000).pipe(
        map(() => 'timeout' as IdleReturnValue),
        tap(() => this.logger.warn('IdleService', 'timeout')())
      ),
      modalRef.closed.pipe(
        map((retVal: IdleReturnValue) =>
          retVal == 'still-here' ? retVal : 'returning'
        )
      )
    ).pipe(take(1));
  }

  agentInteraction() {
    this.agentInteractionSub.next();
  }

  returnToWork() {
    if (this.preIdleState) {
      this.coreSvc.state = this.preIdleState;
      this.preIdleState = undefined;
    }
  }
}
